import React, { useMemo } from 'react';
import { Box, useTheme, Grid } from '@mui/material';
import { SyncAlt } from '@mui/icons-material';
import * as Yup from 'yup';
import GenericDialog from '../GenericDialog/GenericDialog';
import CustomTypography from 'components/Fileds/CustomTypography';
import CustomSelect from 'components/Fileds/CustomSelect';
import CustomTextArea from 'components/Fileds/CustomTextArea';
import { useGetAvailableTablesQuery } from 'pages/tables/services';

const ChangeTableSchema = Yup.object().shape({
    newTableId: Yup.string().required('Please select a table'),
    reason: Yup.string().optional(),
});

const ChangeTableModal = ({ open, onClose, tableData, onSubmit, loading }) => {
    const theme = useTheme();

    // Fetch available tables
    const { data: availableTablesResponse, isLoading: isFetchingTables } = useGetAvailableTablesQuery(undefined, {
        skip: !open,
        refetchOnMountOrArgChange: true
    });

    const availableTables = availableTablesResponse?.data || [];

    const tableOptions = useMemo(() => {
        return availableTables.map(t => ({
            value: t.id,
            label: `${t.table_no} - ${t.capacity} seats`
        }));
    }, [availableTables]);

    const handleSubmit = (values) => {
        onSubmit({
            orderId: tableData?.reservation?.orderId,
            oldTableId: tableData?.id,
            newTableId: values.newTableId,
            reason: values.reason
        });
    };

    const titleIcon = (
        <SyncAlt sx={{ color: '#E86400', fontSize: '24px' }} />
    );

    return (
        <GenericDialog
            open={open}
            onClose={onClose}
            title="Change Table"
            titleIcon={titleIcon}
            centerTitle={false}
            showCancel={true}
            cancelLabel="Cancel"
            initialValues={{
                newTableId: '',
                reason: '',
            }}
            validationSchema={ChangeTableSchema}
            onSubmit={handleSubmit}
            submitLabel="Confirm Change"
            loading={loading || isFetchingTables}
            requiredFields={['newTableId']}
            className='change-table-container'
        >
            {({ values, errors, touched, handleChange, handleBlur }) => (
                <Box>
                    <Box className="move-info-container" sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <CustomTypography
                                label="Move "
                                sx={{ fontSize: '14px', color: '#6A7181', mb: 0 }}
                            />
                            <CustomTypography
                                label={tableData?.table_no}
                                sx={{ fontSize: '14px', color: '#131720', fontWeight: 700, mb: 0, mx: 0.5 }}
                            />
                            <CustomTypography
                                label={`(Order #${tableData?.reservation?.order_id}) to another table.`}
                                sx={{ fontSize: '14px', color: '#6A7181', mb: 0 }}
                            />
                        </Box>

                        <Box sx={{
                            bgcolor: '#F0f4F280',
                            p: 1.5,
                            borderRadius: '10px',
                            border: '1px solid #F1F5F9'
                        }}>
                            <CustomTypography label="Current Table" sx={{ fontSize: '12px', color: '#6A7181', fontWeight: 500, mb: 0.5, letterSpacing: '0.05em' }} />
                            <CustomTypography label={`${tableData?.table_no} • ${tableData?.totalCapacity || tableData?.capacity} seats`} sx={{ fontSize: '14px', fontWeight: 600, color: '#131720', mb: 0.5 }} />
                            <CustomTypography label={`Order #${tableData?.reservation?.order_id}`} sx={{ fontSize: '12px', color: '#6A7181', fontWeight: 500, mb: 0 }} />
                        </Box>
                    </Box>

                    <Box sx={{ mb: 2 }}>
                        <CustomTypography
                            label="Move to Table"
                            sx={{ fontSize: '14px', fontWeight: 500, color: '#131720', mb: 0.5 }}
                        />
                        <CustomSelect
                            name="newTableId"
                            value={values.newTableId}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            placeholder="Select available table"
                            options={tableOptions}
                            error={touched.newTableId && errors.newTableId}
                            helperText={touched.newTableId && errors.newTableId}
                            disabled={isFetchingTables}
                        />
                    </Box>

                    <Box sx={{ mb: 1 }}>
                        <CustomTypography
                            label="Reason (optional)"
                            sx={{ fontSize: '14px', fontWeight: 500, color: '#131720', mb: 0.5 }}
                        />
                        <CustomTextArea
                            name="reason"
                            value={values.reason}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            placeholder="e.g. Customer requested window seat"
                            minRows={3}
                        />
                    </Box>
                </Box>
            )}
        </GenericDialog>
    );
};

export default ChangeTableModal;
